/**
 * DataDisplay Component
 * 
 * Main component for displaying and managing office employee salary and bonus data.
 * Provides functionality for:
 * - Viewing salary and bonus data in tables
 * - Filtering data by year, month (salary) / period (bonus), and employee code
 * - Switching between salary and bonus data types
 * - Deleting data (by month, year, period, employee, or individual records)
 * - Loading and error state management
 * 
 * Features:
 * - Data type toggle (salary/bonus)
 * - Dynamic filtering with real-time data updates
 * - Multiple delete operations (month, year, period, employee, individual)
 * - Loading and error indicators
 * - Delete operation feedback messages
 * 
 * @component
 */

import React, { useState, useCallback } from 'react';
import '../../css/DataDisplay.css';
import { DataType } from './types/DataDisplayTypes';
import { DataTable } from './DataTable';
import { DeleteOperations } from './DeleteOperations';
import { DataTypeToggle } from './DataTypeToggle';
import { DeleteMessages } from './DeleteMessages';
import { LoadingAndError } from './LoadingAndError';
import { useDataFetching } from './hooks/useDataFetching';
import { useDeleteOperations } from './hooks/useDeleteOperations';

/**
 * DataDisplay Component
 * 
 * Main display component for office employee payslip data.
 * Manages filtering, data fetching, and delete operations.
 * 
 * @returns React functional component
 */
export const DataDisplay = () => {
  /**
   * State management:
   * - dataType: Current data type ('salary' or 'bonus')
   * - filterYear: Selected year filter (empty string = all years)
   * - filterMonth: Selected month filter for salary (empty string = all months)
   * - filterPeriod: Selected period filter for bonus (empty string = all periods)
   * - filterEmployeeCode: Selected employee code filter (empty string = all employees)
   */
  const [dataType, setDataType] = useState<DataType>('salary');
  const [filterMonth, setFilterMonth] = useState<string>('');
  const [filterYear, setFilterYear] = useState<string>('');
  const [filterPeriod, setFilterPeriod] = useState<string>('');
  const [filterEmployeeCode, setFilterEmployeeCode] = useState<string>('');

  /**
   * Custom hook for data fetching.
   * 
   * Provides:
   * - salaryData: Array of salary records
   * - bonusData: Array of bonus records
   * - loading: Loading state indicator
   * - error: Error message if fetch fails
   * - fetchFilteredData: Function to fetch filtered data
   */
  const {
    salaryData,
    bonusData,
    allSalaryData,
    allBonusData,
    loading,
    error,
    fetchFilteredData,
    refreshFilterOptions,
  } = useDataFetching(dataType);

  const filterOptionsData = dataType === 'salary' ? allSalaryData : allBonusData;

  const resetToFullListAfterDelete = useCallback(() => {
    setFilterYear('');
    setFilterMonth('');
    setFilterPeriod('');
    setFilterEmployeeCode('');
    fetchFilteredData();
    refreshFilterOptions();
  }, [fetchFilteredData, refreshFilterOptions]);

  /**
   * Custom hook for delete operations.
   * 
   * Provides:
   * - deleteLoading: Loading state for delete operations
   * - deleteError: Error message if delete fails
   * - deleteSuccess: Success message after delete
   * - handleDeleteMonthData: Delete all data for a month
   * - handleDeleteYearData: Delete all data for a year
   * - handleDeleteEmployeeData: Delete all data for an employee
   * - handleDeletePeriodData: Delete all data for a period (bonus)
   * - handleDeleteIndividualRecord: Delete a single record
   * - clearDeleteMessages: Clear delete operation messages
   * 
   * Callback refetches data after successful delete.
   */
  const {
    deleteLoading,
    deleteError,
    deleteSuccess,
    handleDeleteYearData,
    handleDeleteMonthData,
    handleDeletePeriodData,
    handleDeleteSelection,
    handleDeleteIndividualRecord,
    clearDeleteMessages
  } = useDeleteOperations(dataType, resetToFullListAfterDelete);

  // Handle data type toggle
  const handleDataTypeToggle = () => {
    const newDataType = dataType === 'salary' ? 'bonus' : 'salary';
    setDataType(newDataType);
    // Clear filters when switching data types
    setFilterYear('');
    setFilterMonth('');
    setFilterPeriod('');
    setFilterEmployeeCode('');
  };

  // Handle filter changes
  const handleYearChange = (newYear: string) => {
    setFilterYear(newYear);
    if (dataType === 'salary') {
      fetchFilteredData(newYear, filterMonth, undefined, filterEmployeeCode);
    } else {
      fetchFilteredData(newYear, undefined, filterPeriod, filterEmployeeCode);
    }
  };

  /**
   * Handles month filter change (for salary data).
   * 
   * Updates month filter and fetches filtered data.
   * Only used when dataType is 'salary'.
   * 
   * @param newMonth - Selected month string
   */
  const handleMonthChange = (newMonth: string) => {
    setFilterMonth(newMonth);
    fetchFilteredData(filterYear, newMonth, undefined, filterEmployeeCode);
  };

  /**
   * Handles period filter change (for bonus data).
   * 
   * Updates period filter and fetches filtered data.
   * Only used when dataType is 'bonus'.
   * 
   * @param newPeriod - Selected period string
   */
  const handlePeriodChange = (newPeriod: string) => {
    setFilterPeriod(newPeriod);
    fetchFilteredData(filterYear, undefined, newPeriod, filterEmployeeCode);
  };

  /**
   * Handles employee code filter change.
   * 
   * Updates employee code filter and immediately fetches filtered data.
   * Passes appropriate parameters based on data type:
   * - Salary: year, month, undefined (period), employeeCode
   * - Bonus: year, undefined (month), period, employeeCode
   * 
   * @param newEmployeeCode - Selected employee code string
   */
  const handleEmployeeCodeChange = (newEmployeeCode: string) => {
    setFilterEmployeeCode(newEmployeeCode);
    if (dataType === 'salary') {
      fetchFilteredData(filterYear, filterMonth, undefined, newEmployeeCode);
    } else {
      fetchFilteredData(filterYear, undefined, filterPeriod, newEmployeeCode);
    }
  };

  /**
   * Clears all filters and fetches all data.
   * 
   * Resets all filter states to empty strings and calls fetchFilteredData
   * with no parameters to retrieve all records.
   */
  const handleClearFilters = () => {
    setFilterYear('');
    setFilterMonth('');
    setFilterPeriod('');
    setFilterEmployeeCode('');
    fetchFilteredData();
    refreshFilterOptions();
  };

  /**
   * Delete operation handlers.
   * 
   * Wrapper functions that call delete handlers with current filter values.
   * These are passed to DeleteOperations component as props.
   */
  const onDeleteYearData = () => handleDeleteYearData(filterYear);
  const onDeleteMonthData = () => handleDeleteMonthData(filterYear, filterMonth);
  const onDeletePeriodData = () => handleDeletePeriodData(filterYear, filterPeriod);
  const onDeleteSelection = () =>
    handleDeleteSelection(filterYear, filterMonth, filterPeriod, filterEmployeeCode);

  /**
   * Get filtered data based on current data type.
   * 
   * Returns salaryData if dataType is 'salary', otherwise returns bonusData.
   * This is used to populate filter dropdowns and display in DataTable.
   */
  const filteredData = dataType === 'salary' ? salaryData : bonusData;

  return (
    <div className="data-display-container" style={{ position: 'relative' }}>
      {/* Loading and Error Display */}
      <LoadingAndError loading={loading} error={error} />
      
      {/* Data Type Toggle - Switch between Salary (給与) and Bonus (賞与) */}
      <DataTypeToggle dataType={dataType} onToggle={handleDataTypeToggle} />

      {/* Controls Section - Filters and Delete Operations */}
      <div className="controls-section">
        {/* Filter Operations */}
        <div className="filter-operations">
          {/* Year Filter */}
          <div className="filter-group">
            <label htmlFor="year-filter">年:</label>
            <select 
              id="year-filter"
              value={filterYear} 
              onChange={(e) => handleYearChange(e.target.value)}
            >
              <option value="">すべて</option>
              {Array.from(new Set(filterOptionsData.map(d => d.year))).sort((a, b) => b - a).map(year => (
                <option key={year} value={year}>{year}年</option>
              ))}
            </select>
          </div>

          {/* Month Filter (for Salary) or Period Filter (for Bonus) */}
          {dataType === 'salary' ? (
            /**
             * Month Filter - Only shown for salary data type.
             * Shows available months from filtered data, sorted ascending.
             */
            <div className="filter-group">
              <label htmlFor="month-filter">月:</label>
              <select 
                id="month-filter"
                value={filterMonth} 
                onChange={(e) => handleMonthChange(e.target.value)}
              >
                <option value="">すべて</option>
                {Array.from(new Set((filterOptionsData as any[]).map(d => d.month))).sort((a, b) => a - b).map(month => (
                  <option key={month} value={month}>{month}月</option>
                ))}
              </select>
            </div>
          ) : (
            /**
             * Period Filter - Only shown for bonus data type.
             * Shows available periods from filtered data, sorted alphabetically.
             */
            <div className="filter-group">
              <label htmlFor="period-filter">期:</label>
              <select 
                id="period-filter"
                value={filterPeriod} 
                onChange={(e) => handlePeriodChange(e.target.value)}
              >
                <option value="">すべて</option>
                {Array.from(new Set((filterOptionsData as any[]).map(d => d.bonusPeriod))).sort((a, b) => a.localeCompare(b)).map(period => (
                  <option key={period} value={period}>{period}</option>
                ))}
              </select>
            </div>
          )}

          {/* Employee Code Filter */}
          <div className="filter-group">
            <label htmlFor="employee-code-filter">従業員コード:</label>
            <select
              id="employee-code-filter"
              value={filterEmployeeCode}
              onChange={(e) => handleEmployeeCodeChange(e.target.value)}
              className="employee-code-select"
            >
              <option value="">すべて</option>
              {Array.from(new Set(filterOptionsData.map(d => d.employeeCode))).sort((a, b) => a.localeCompare(b)).map(empCode => (
                <option key={empCode} value={empCode}>{empCode}</option>
              ))}
            </select>
          </div>

          {/* Clear Filters Button */}
          <button 
            className="clear-filters"
            onClick={handleClearFilters}
          >
            フィルタークリア
          </button>
        </div>

        {/* Delete Operations Component */}
        <DeleteOperations
          dataType={dataType}
          filterYear={filterYear}
          filterMonth={filterMonth}
          filterPeriod={filterPeriod}
          filterEmployeeCode={filterEmployeeCode}
          deleteLoading={deleteLoading}
          onDeleteYearData={onDeleteYearData}
          onDeleteMonthData={onDeleteMonthData}
          onDeletePeriodData={onDeletePeriodData}
          onDeleteSelection={onDeleteSelection}
        />
      </div>

      {/* Delete Messages - Shows success/error messages for delete operations */}
      <DeleteMessages
        deleteError={deleteError}
        deleteSuccess={deleteSuccess}
        onClearMessages={clearDeleteMessages}
      />

      {/* Data Table - Displays filtered data with delete individual record functionality */}
      <DataTable
        dataType={dataType}
        filteredData={filteredData}
        deleteLoading={deleteLoading}
        onDeleteIndividualRecord={(employeeCode, year, month, employeeName, period) => 
          handleDeleteIndividualRecord(employeeCode, year, month, employeeName, period)
        }
      />
    </div>
  );
}; 